import { z } from "zod";
import type { HttpClient } from "./http";
import { platformFeaturesSchema, workspaceModulesSchema, type ModuleSettingsUpdate } from "./modules";
import {
  auditEventSchema,
  domainSchema,
  domainTargetSchema,
  meSchema,
  memberSchema,
  organizationSchema,
  pageOf,
  productSchema,
  provisioningRequestSchema,
  sessionSchema,
  subscriptionSchema,
  tokensSchema,
  workspaceSchema,
  workspaceTokenSchema,
  type OrganizationRole,
  type ProvisioningKind,
} from "./schemas";

/** Every endpoint the customer console uses, typed end to end. */
export function consoleApi(http: HttpClient) {
  return {
    features: () => http.request("/api/v1/identity/features", { schema: platformFeaturesSchema }),

    workspaceModules: (workspaceId: string) =>
      http.request("/api/v1/console/workspaces/" + encodeURIComponent(workspaceId) + "/modules", {
        schema: workspaceModulesSchema,
      }),

    setWorkspaceModules: (workspaceId: string, body: ModuleSettingsUpdate) =>
      http.request("/api/v1/console/workspaces/" + encodeURIComponent(workspaceId) + "/modules", {
        method: "PUT", body, schema: workspaceModulesSchema,
      }),

    me: () => http.request("/api/v1/console/me", { schema: meSchema }),

    organization: () =>
      http.request("/api/v1/console/organization", { schema: organizationSchema }),

    updateOrganization: (body: {
      name: string;
      legalName?: string | null;
      country?: string | null;
      billingEmail?: string | null;
    }) =>
      http.request("/api/v1/console/organization", {
        method: "PATCH",
        body,
        schema: organizationSchema,
      }),

    members: () =>
      http.request("/api/v1/console/members", { schema: z.array(memberSchema) }),

    addMember: (body: { email: string; role: OrganizationRole }) =>
      http.request("/api/v1/console/members", { method: "POST", body, schema: memberSchema }),

    changeRole: (memberId: string, role: OrganizationRole) =>
      http.request<void>(`/api/v1/console/members/${memberId}`, { method: "PATCH", body: { role } }),

    removeMember: (memberId: string) =>
      http.request<void>(`/api/v1/console/members/${memberId}`, { method: "DELETE" }),

    products: () =>
      http.request("/api/v1/console/products", { schema: z.array(productSchema) }),

    subscriptions: () =>
      http.request("/api/v1/console/subscriptions", { schema: z.array(subscriptionSchema) }),

    workspaces: () =>
      http.request("/api/v1/console/workspaces", { schema: z.array(workspaceSchema) }),

    requests: () =>
      http.request("/api/v1/console/requests", { schema: z.array(provisioningRequestSchema) }),

    submitRequest: (body: { kind: ProvisioningKind; payload?: string; note?: string }) =>
      http.request("/api/v1/console/requests", {
        method: "POST",
        body,
        schema: provisioningRequestSchema,
      }),

    cancelRequest: (requestId: string) =>
      http.request<void>(`/api/v1/console/requests/${requestId}`, { method: "DELETE" }),

    audit: (page = 0, size = 25) =>
      http.request("/api/v1/console/audit", {
        query: { page, size },
        schema: pageOf(auditEventSchema),
      }),

    sessions: () =>
      http.request("/api/v1/console/sessions", { schema: z.array(sessionSchema) }),

    revokeSession: (sessionId: string) =>
      http.request<void>(`/api/v1/console/sessions/${sessionId}`, { method: "DELETE" }),

    revokeAllSessions: () =>
      http.request<void>("/api/v1/console/sessions/revoke-all", { method: "POST" }),

    changePassword: (body: { currentPassword: string; newPassword: string }) =>
      http.request<void>("/api/v1/console/password", { method: "POST", body }),

    domains: () => http.request("/api/v1/console/domains", { schema: z.array(domainSchema) }),

    domainTargets: (workspaceId: string) =>
      http.request(`/api/v1/console/workspaces/${workspaceId}/domain-targets`, {
        schema: z.array(domainTargetSchema),
      }),

    addDomain: (body: {
      workspaceId: string;
      hostname: string;
      kind?: string;
      targetRef?: string;
    }) =>
      http.request("/api/v1/console/domains", { method: "POST", body, schema: domainSchema }),

    removeDomain: (domainId: string) =>
      http.request<void>(`/api/v1/console/domains/${domainId}`, { method: "DELETE" }),

    workspaceToken: (workspaceId: string) =>
      http.request("/api/v1/identity/workspace-token", {
        method: "POST",
        body: { workspaceId },
        schema: workspaceTokenSchema,
      }),

    refresh: (refreshToken: string) =>
      http.request("/api/v1/identity/refresh", {
        method: "POST",
        body: { refreshToken },
        schema: tokensSchema,
      }),
  };
}

export type ConsoleApi = ReturnType<typeof consoleApi>;
